import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@jobenvoy/ui";
import { useSession } from "@/auth/session";
import { trackPurchase } from "@/lib/ads";
import { track } from "@/lib/analytics";
import { trackConversion } from "@/lib/conversions";
import { leaveFor } from "@/lib/leave";
import { STRIPE_HOSTS } from "@/lib/safeUrl";
import { describeError } from "@/lib/api/client";
import {
    packReturnQuery,
    planReturnQuery,
    purchaseEventOf,
    purchaseOf,
    type Bought,
} from "@/lib/purchases";
import { ROUTES } from "@/lib/routes";
import {
    getClaims,
    getPlans,
    getUsage,
    openPackCheckout,
    openPlanCheckout,
    openPortal,
    type BillingInterval,
    type BillingPlanList,
    type BillingTier,
} from "@/lib/api/billing";
import { keys } from "./keys";
import { ONE_AT_A_TIME, useSingleFlightMutation } from "./singleFlight";

/** How often claims are asked again while billing sets a new account up, which takes seconds. */
export const ONBOARDING_POLL_MS = 2_000;

/**
 * What the signed-in person has used and has left this period.
 *
 * Asked only once claims say billing has set the person up (or claims
 * failed, so the page is not held on them). Before then billing answers
 * a zero allowance, which would read as "no tailors left" on a new
 * account, so usage stays pending instead.
 */
export function useUsage() {
    const { userId } = useSession();
    const claims = useClaims();
    return useQuery({
        queryKey: keys.billing.usage(userId),
        queryFn: () => getUsage(userId),
        enabled: claims.isError || claims.data?.onboarding === false,
    });
}

/** The signed-in person's plan and what it allows, asked again every two seconds while billing sets them up. */
export function useClaims() {
    const { userId } = useSession();
    return useQuery({
        queryKey: keys.billing.claims(userId),
        queryFn: () => getClaims(userId),
        refetchInterval: (query) => (query.state.data?.onboarding ? ONBOARDING_POLL_MS : false),
    });
}

/** Whether billing is still setting the signed-in person up. */
export function useSettingUp(): boolean {
    return useClaims().data?.onboarding === true;
}

/** The plans and packs on sale, the same for everyone. */
export function usePlans() {
    return useQuery({ queryKey: keys.billing.plans, queryFn: getPlans, staleTime: 10 * 60_000 });
}

/** The tailors the free plan gives a month, from the plans on sale. Null when the list has no free plan. */
export function freeTailorsOf(list: BillingPlanList | undefined): number | null {
    return list?.plans.find((plan) => plan.tier === "free")?.tailors_per_month ?? null;
}

/** The free plan's tailors a month, read from the plans on sale; null until they are read. */
export function useFreeTailors(): number | null {
    return freeTailorsOf(usePlans().data);
}

/**
 * Opens Stripe's customer portal: invoices, the card, and on a paid plan
 * the move to another plan. With a tier it is a plan change (up or down)
 * and is counted as one.
 */
export function usePortal() {
    const { userId } = useSession();
    const toast = useToast();
    return useSingleFlightMutation(
        {
            mutationFn: async (tier?: BillingTier) => {
                const session = await openPortal(userId, window.location.href);
                if (tier) track({ name: "checkout_opened", kind: "plan", item: tier });
                else track({ name: "billing_portal_opened" });
                await leaveFor(session.url, STRIPE_HOSTS);
            },
            onError: (error) => toast.error("Billing could not be opened", describeError(error)),
        },
        ONE_AT_A_TIME,
    );
}

/**
 * Opens Stripe Checkout to start a paid plan from Free, where the billing
 * address is asked for and the tax added. Back on /subscribed once
 * subscribed, naming the plan and Stripe's session so the purchase can
 * be counted, or where it was opened from if not.
 */
export function usePlanCheckout() {
    const { userId } = useSession();
    const toast = useToast();
    return useSingleFlightMutation(
        {
            mutationFn: async ({
                tier,
                interval,
            }: {
                tier: Exclude<BillingTier, "free">;
                interval: BillingInterval;
            }) => {
                const back = `${window.location.origin}${window.location.pathname}`;
                const session = await openPlanCheckout(
                    userId,
                    tier,
                    interval,
                    `${window.location.origin}${ROUTES.SUBSCRIBED}${planReturnQuery(tier, interval)}`,
                    back,
                );
                track({ name: "checkout_opened", kind: "plan", item: tier });
                await leaveFor(session.url, STRIPE_HOSTS);
            },
            onError: (error) => toast.error("Checkout could not be opened", describeError(error)),
        },
        ONE_AT_A_TIME,
    );
}

/** Opens Stripe Checkout for one pack, by its size. Back on /pricing either way, naming the pack once bought. */
export function usePackCheckout() {
    const { userId } = useSession();
    const toast = useToast();
    return useSingleFlightMutation(
        {
            mutationFn: async (pack: number) => {
                const back = `${window.location.origin}${window.location.pathname}`;
                const session = await openPackCheckout(
                    userId,
                    pack,
                    `${back}${packReturnQuery(pack)}`,
                    back,
                );
                track({ name: "checkout_opened", kind: "pack", item: String(pack) });
                await leaveFor(session.url, STRIPE_HOSTS);
            },
            onError: (error) => toast.error("Checkout could not be opened", describeError(error)),
        },
        ONE_AT_A_TIME,
    );
}

/**
 * Counts what the page was opened with as bought, once, for the ad partners
 * (lib/ads) and PostHog (lib/conversions), with its price once the plans
 * are read or have failed.
 * Only the first render's purchase counts: the page drops the address
 * that named it.
 */
export function useCountPurchase(bought: Bought | null) {
    const plans = usePlans();
    const [held] = useState(bought);
    const counted = useRef(false);
    useEffect(() => {
        if (!held || counted.current || plans.isPending) return;
        counted.current = true;
        const purchase = purchaseOf(held, plans.data);
        trackPurchase(purchase);
        trackConversion(purchaseEventOf(held, purchase));
    }, [held, plans.isPending, plans.data]);
}
